import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { services, regionDots } from "../data/services";
import { PageHead } from "../components/PageHead";
import { Reveal } from "../components/Reveal";
import { useTitle } from "../lib/useTitle";

const slugify = (t) => t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "");

export default function ServicePage({ service }) {
  useTitle(`${service.name} | Amplified Minds`);
  const others = services.filter((s) => s.slug !== service.slug);
  const audienceLabel = service.audience === "families" ? "For families" : "For businesses";

  return (
    <>
      <PageHead
        crumbs={[
          { label: "Home", to: "/" },
          { label: "Services", to: "/#services" },
          { label: service.name },
        ]}
        title={service.name}
        lede={service.lede}
      >
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <span className="flex h-[58px] w-[58px] items-center justify-center rounded-2xl" style={{ background: service.soft }} data-testid="service-icon-tile">
            <service.icon className="h-7 w-7" style={{ color: service.color }} aria-hidden="true" />
          </span>
          <div>
            <p className="am-eyebrow" style={{ color: service.color }}>
              {audienceLabel}
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {service.who.map((w) => (
                <span key={w} className="rounded-full border border-line bg-base px-3 py-1 text-xs font-medium text-mute">
                  {w}
                </span>
              ))}
            </div>
          </div>
        </div>
      </PageHead>

      <section className="py-16 md:py-24" data-testid="service-covers">
        <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-3xl font-bold text-ink sm:text-4xl">
              What {service.short} covers
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {service.covers.map((c, i) => (
              <Reveal key={c.title} delay={(i % 3) * 0.07}>
                <article
                  id={slugify(c.title)}
                  data-testid={`cover-${slugify(c.title)}`}
                  className="flex h-full scroll-mt-28 flex-col rounded-[14px] border border-line bg-surface p-6 shadow-surface"
                >
                  <h3 className="font-display text-lg font-bold text-ink">{c.title}</h3>
                  <p className="mt-2.5 flex-1 text-sm leading-relaxed text-mute">{c.desc}</p>
                  <div className="mt-5 flex gap-1.5" aria-hidden="true">
                    {regionDots.map((d) => (
                      <span key={d} className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: d }} />
                    ))}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-ink">Our other services</h2>
          </Reveal>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.07}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`other-service-${s.slug}`}
                  className="group flex h-full items-center gap-3 rounded-[14px] border border-line bg-surface p-4 shadow-surface transition-all duration-200 hover:-translate-y-0.5 hover:border-ink"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ background: s.soft }}>
                    <s.icon className="h-5 w-5" style={{ color: s.color }} aria-hidden="true" />
                  </span>
                  <span className="flex-1 text-sm font-semibold leading-snug text-ink">{s.name}</span>
                  <ArrowRight className="h-4 w-4 text-mute transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="relative mt-12 overflow-hidden rounded-[24px] p-8 sm:p-12" style={{ background: "var(--ink)" }} data-testid="service-cta">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full opacity-35 blur-3xl"
                style={{ background: "conic-gradient(from 90deg, var(--emerald), var(--blue), var(--coral), var(--saffron), var(--emerald))" }}
              />
              <div className="relative flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
                <h2 className="font-display text-2xl font-bold sm:text-3xl" style={{ color: "var(--bg)" }}>
                  Talk to our {service.short} team.
                </h2>
                <Link
                  to={`/contact?service=${encodeURIComponent(service.name)}`}
                  className="am-btn shrink-0 px-7 py-3.5 text-sm"
                  data-testid="service-cta-button"
                >
                  Get in touch
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
